import { useState, useEffect, useCallback } from "react";
import { Bell, CheckCircle2, Clock, MessageSquare, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatDistanceToNow } from "date-fns";

interface Notification {
  id: string;
  type: 'approval' | 'support';
  title: string;
  subtitle: string;
  timestamp: string;
  isNew: boolean;
  tab: 'campaigns' | 'support';
}

interface NotificationsCenterProps {
  mediaOwnerName: string;
  onNavigateToTab: (tab: 'campaigns' | 'support') => void;
}

export const NotificationsCenter = ({ mediaOwnerName, onNavigateToTab }: NotificationsCenterProps) => {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const storageKey = `mo_notifications_seen_${mediaOwnerName}`;

  const getLastSeen = () => localStorage.getItem(storageKey) || new Date(0).toISOString();
  const markAllRead = () => {
    localStorage.setItem(storageKey, new Date().toISOString());
    setNotifications(prev => prev.map(n => ({ ...n, isNew: false })));
    setUnreadCount(0);
  };

  const fetchNotifications = useCallback(async () => {
    const lastSeen = getLastSeen();

    const [approvalsRes, supportRes] = await Promise.all([
      supabase
        .from('media_owner_campaign_approvals')
        .select('id, status, created_at, campaign_id')
        .eq('media_owner_name', mediaOwnerName)
        .order('created_at', { ascending: false })
        .limit(8),
      supabase
        .from('media_owner_support_requests')
        .select('id, subject, status, created_at, updated_at, admin_reply')
        .eq('media_owner_name', mediaOwnerName)
        .order('updated_at', { ascending: false })
        .limit(5),
    ]);

    const approvalNotifs: Notification[] = (approvalsRes.data || []).map(a => ({
      id: `approval-${a.id}`,
      type: 'approval' as const,
      title: a.status === 'pending'
        ? (language === 'ar' ? 'حملة جديدة تنتظر الموافقة' : 'New campaign awaiting approval')
        : a.status === 'approved'
        ? (language === 'ar' ? 'تمت الموافقة على الحملة' : 'Campaign approved')
        : (language === 'ar' ? 'تم رفض الحملة' : 'Campaign rejected'),
      subtitle: `ID: ${a.campaign_id.slice(0, 8)}...`,
      timestamp: a.created_at,
      isNew: a.created_at > lastSeen,
      tab: 'campaigns' as const,
    }));

    const supportNotifs: Notification[] = (supportRes.data || []).map(s => ({
      id: `support-${s.id}`,
      type: 'support' as const,
      title: s.admin_reply
        ? (language === 'ar' ? 'تم الرد على طلب الدعم' : 'Support request answered')
        : (language === 'ar' ? 'تم إرسال طلب الدعم' : 'Support request submitted'),
      subtitle: s.subject,
      timestamp: s.updated_at || s.created_at,
      isNew: (s.updated_at || s.created_at) > lastSeen,
      tab: 'support' as const,
    }));

    const all = [...approvalNotifs, ...supportNotifs]
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
      .slice(0, 10);

    setNotifications(all);
    setUnreadCount(all.filter(n => n.isNew).length);
  }, [mediaOwnerName]); // intentionally exclude `language` — labels are evaluated inside and should not trigger a refetch on language change

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // Auto-mark read when popover opens
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      setTimeout(() => markAllRead(), 2000); // mark read after 2s of viewing
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-xl border border-border/60 bg-card hover:bg-accent transition-colors shadow-sm">
          <Bell className="h-4 w-4 text-foreground" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold flex items-center justify-center leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-2xl shadow-xl border-border/60 overflow-hidden bg-white dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-gray-50 dark:bg-gray-800">
          <div>
            <p className="text-sm font-semibold text-foreground">{language === 'ar' ? 'الإشعارات' : 'Notifications'}</p>
            {unreadCount > 0 && (
              <p className="text-xs text-muted-foreground">{unreadCount} {language === 'ar' ? 'جديد' : 'new'}</p>
            )}
          </div>
          {notifications.some(n => n.isNew) && (
            <button
              onClick={markAllRead}
              className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
            >
              {language === 'ar' ? 'تحديد الكل كمقروء' : 'Mark all read'}
            </button>
          )}
        </div>

        {/* Notifications list */}
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center px-4">
              <div className="p-3 rounded-2xl bg-muted/50 mb-2">
                <Bell className="h-6 w-6 text-muted-foreground opacity-40" />
              </div>
              <p className="text-sm text-muted-foreground">{language === 'ar' ? 'لا توجد إشعارات' : 'No notifications yet'}</p>
            </div>
          ) : (
            notifications.map(notif => (
              <button
                key={notif.id}
                onClick={() => { onNavigateToTab(notif.tab); setOpen(false); }}
                className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-accent/50 transition-colors text-left border-b border-border/30 last:border-0
                  ${notif.isNew ? 'bg-primary/3' : ''}`}
              >
                {/* Icon */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center mt-0.5
                  ${notif.type === 'approval'
                    ? 'bg-emerald-100 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400'
                    : 'bg-blue-100 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400'
                  }`}>
                  {notif.type === 'approval'
                    ? <CheckCircle2 className="h-4 w-4" />
                    : <MessageSquare className="h-4 w-4" />
                  }
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-xs font-medium leading-snug ${notif.isNew ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {notif.title}
                    </p>
                    {notif.isNew && <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-primary mt-1" />}
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate mt-0.5">{notif.subtitle}</p>
                  <p className="text-[10px] text-muted-foreground/60 mt-1 flex items-center gap-1">
                    <Clock className="h-2.5 w-2.5" />
                    {formatDistanceToNow(new Date(notif.timestamp), { addSuffix: true })}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
